/**
 * Returns whether the event target is an element whose content the user can edit
 * directly, that is one with `isContentEditable` set to `true`.
 * @public
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  // `isContentEditable` is undefined instead of false in environments that do not
  // implement `contenteditable`, so the result is normalized to a boolean.
  return Boolean(target instanceof HTMLElement && target.isContentEditable);
}

/**
 * Returns whether the event target is an `<input>` element.
 * @public
 */
export function isInputTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLInputElement;
}

/**
 * Returns whether the event target is an element with `role="textbox"`.
 * @public
 */
export function isTextboxTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement && target.getAttribute('role') === 'textbox'
  );
}

/**
 * Returns whether the event target is a `<textarea>` element.
 * @public
 */
export function isTextareaTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLTextAreaElement;
}

/**
 * Returns whether the event target is a `<select>` element.
 * @public
 */
export function isSelectTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLSelectElement;
}

/**
 * Returns whether the event target is a value-entry control, that is an editable
 * element, an `<input>`, an element with `role="textbox"`, a `<textarea>`, or a
 * `<select>`. `useKeyboardShortcut` suppresses a shortcut for these targets by
 * default.
 * @public
 */
export function isValueEntryTarget(target: EventTarget | null): boolean {
  return (
    isEditableTarget(target) ||
    isInputTarget(target) ||
    isTextboxTarget(target) ||
    isTextareaTarget(target) ||
    isSelectTarget(target)
  );
}
